import { useCallback, useEffect, useRef } from "react";

const LONG_PRESS_MS = 500;
// A finger that drifts this far is scrolling the list, not pressing an item.
const MOVE_TOLERANCE_PX = 10;

/**
 * Long-press (touch) and right-click (mouse) on a menu item or cart line.
 *
 *   const press = useItemPress({ onLongPress, onContextMenu });
 *   <div {...press.bind(item)}>…</div>
 *
 * Touch: holding a finger still for half a second calls `onLongPress(item)`, and
 * the tap that the lift would otherwise produce is swallowed — so a long-press
 * on a card never also punches the dish its ADD button sits on. A finger that
 * moves (scrolling) cancels it. The phone's own long-press menu is suppressed.
 *
 * Mouse: a long-press means nothing (a held click is a click). Right-click calls
 * `onContextMenu(item, {x, y})` instead of opening the browser's menu.
 */
export function useItemPress({ onLongPress, onContextMenu, delay = LONG_PRESS_MS } = {}) {
  const timer = useRef(null);
  const start = useRef(null);
  const fired = useRef(false);
  const lastPointer = useRef(null);
  const handlers = useRef({ onLongPress, onContextMenu });
  handlers.current = { onLongPress, onContextMenu };

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const bind = useCallback(
    payload => ({
      onPointerDown: event => {
        lastPointer.current = event.pointerType || "touch";
        fired.current = false;
        if (event.pointerType === "mouse" || !handlers.current.onLongPress) return;
        cancel();
        start.current = { x: event.clientX || 0, y: event.clientY || 0 };
        timer.current = setTimeout(() => {
          timer.current = null;
          fired.current = true;
          try {
            navigator.vibrate?.(15);
          } catch {
            // Vibration is a nicety; some WebViews throw on it.
          }
          handlers.current.onLongPress?.(payload);
        }, delay);
      },
      onPointerMove: event => {
        if (!start.current) return;
        const dx = Math.abs((event.clientX || 0) - start.current.x);
        const dy = Math.abs((event.clientY || 0) - start.current.y);
        if (dx > MOVE_TOLERANCE_PX || dy > MOVE_TOLERANCE_PX) cancel();
      },
      onPointerUp: cancel,
      onPointerCancel: cancel,
      onPointerLeave: cancel,
      onClickCapture: event => {
        if (!fired.current) return;
        fired.current = false;
        event.preventDefault();
        event.stopPropagation();
      },
      onContextMenu: event => {
        // Android fires contextmenu on a long-press too: that one is ours (or a
        // scroll), never the browser's copy/select menu.
        if (lastPointer.current && lastPointer.current !== "mouse") {
          event.preventDefault();
          return;
        }
        if (!handlers.current.onContextMenu) return;
        event.preventDefault();
        handlers.current.onContextMenu(payload, { x: event.clientX, y: event.clientY });
      },
    }),
    [cancel, delay]
  );

  return { bind };
}
